import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { UserPlus } from 'lucide-react';
import type { RegisterRequestToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { formatDate } from '~/lib/format-date';
import { serverMessage } from '~/lib/api/server-error';
import { useRegisterRequestsList, useRegisterRequestMutations } from '~/hooks/use-register-requests';
import { RegisterRequestEditDialog } from './register-request-edit-dialog';

/**
 * Register requests (Αιτήσεις Εγγραφής) — pending customer signups submitted from the storefront.
 * Parity with the legacy admin grid: read / edit / delete only (records are storefront-created,
 * so there is no add path), with the company/contact fields available via the column selector.
 */
export function RegisterRequestsGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useRegisterRequestsList();
	const { remove } = useRegisterRequestMutations();

	const [editRow, setEditRow] = useState<RegisterRequestToken | null>(null);
	const [editOpen, setEditOpen] = useState(false);
	const [deleteRow, setDeleteRow] = useState<RegisterRequestToken | null>(null);

	const col = (key: string) => t(`customers.registerRequests.columns.${key}`);

	const columns = useMemo<SystemGridColumn<RegisterRequestToken>[]>(
		() => [
			{
				key: 'eml',
				header: col('email'),
				width: 'minmax(0,1.4fr)',
				sortValue: o => o.eml ?? '',
				exportValue: o => o.eml ?? '',
				render: o => <span className="truncate text-sm font-medium text-foreground">{o.eml || '—'}</span>
			},
			{
				key: 'fn',
				header: col('name'),
				width: 'minmax(0,1fr)',
				sortValue: o => o.fn ?? '',
				exportValue: o => o.fn ?? '',
				render: o => <span className="truncate text-sm text-foreground-muted">{o.fn || '—'}</span>
			},
			{
				key: 'ln',
				header: col('surname'),
				width: 'minmax(0,1fr)',
				sortValue: o => o.ln ?? '',
				exportValue: o => o.ln ?? '',
				render: o => <span className="truncate text-sm text-foreground-muted">{o.ln || '—'}</span>
			},
			{
				key: 'cnm',
				header: col('company'),
				width: 'minmax(0,1fr)',
				sortValue: o => o.cnm ?? '',
				exportValue: o => o.cnm ?? '',
				render: o => <span className="truncate text-sm text-foreground-muted">{o.cnm || '—'}</span>
			},
			{
				key: 'vat',
				header: col('vat'),
				width: '130px',
				sortValue: o => o.vat ?? '',
				exportValue: o => o.vat ?? '',
				render: o => <span className="truncate text-sm tabular-nums text-foreground-muted">{o.vat || '—'}</span>
			},
			{
				key: 'tel',
				header: col('tel'),
				width: '140px',
				sortValue: o => o.tel ?? '',
				exportValue: o => o.tel ?? '',
				render: o => <span className="truncate text-sm tabular-nums text-foreground-muted">{o.tel || '—'}</span>
			},
			{
				key: 'mob',
				header: col('mobile'),
				width: '140px',
				defaultVisible: false,
				sortValue: o => o.mob ?? '',
				exportValue: o => o.mob ?? '',
				render: o => <span className="truncate text-sm tabular-nums text-foreground-muted">{o.mob || '—'}</span>
			},
			{
				key: 'irs',
				header: col('irs'),
				width: '160px',
				defaultVisible: false,
				sortValue: o => o.irs ?? '',
				exportValue: o => o.irs ?? '',
				render: o => <span className="truncate text-sm text-foreground-muted">{o.irs || '—'}</span>
			},
			{
				key: 'cdt',
				header: col('createDate'),
				width: '170px',
				sortValue: o => (o.cdt as string | null) ?? '',
				exportValue: o => formatDate(o.cdt as string | null),
				render: o => <span className="text-sm tabular-nums text-foreground-muted">{(o.cdt && formatDate(o.cdt as string)) || '—'}</span>
			}
		],
		[t]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: (err: unknown) => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<RegisterRequestToken>
				gridId="register-requests"
				title={t('customers.registerRequests.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={o => o.id ?? o.eml ?? ''}
				columns={columns}
				searchPredicate={(o, q) =>
					(o.eml ?? '').toLowerCase().includes(q) ||
					(o.fn ?? '').toLowerCase().includes(q) ||
					(o.ln ?? '').toLowerCase().includes(q) ||
					(o.cnm ?? '').toLowerCase().includes(q) ||
					(o.vat ?? '').toLowerCase().includes(q)
				}
				defaultSort={{ key: 'cdt', dir: 'desc' }}
				emptyIcon={<UserPlus size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				onEdit={o => {
					setEditRow(o);
					setEditOpen(true);
				}}
				onDelete={setDeleteRow}
				exportFilename="register-requests"
			/>

			<RegisterRequestEditDialog open={editOpen} onOpenChange={setEditOpen} row={editRow} />

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('customers.registerRequests.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('customers.registerRequests.deleteBody', { name: deleteRow?.eml || '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
